import { signal } from '@preact/signals-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createRoot, type Root } from 'react-dom/client';
import { configureApiClient } from './api/client';
import { featurePayloadSignal } from './api/feature-payload';
import { paramsSignal } from './api/params';
import { WidgetShell } from './widget-shell/WidgetShell';
import type { AppParams, FeatureKey, TutorWidgetAPI } from './types';

import resetCss from './styles/reset.css?inline';
import tokensCss from './styles/tokens.css?inline';
import globalsCss from './styles/globals.css?inline';

const openFeatureSignal = signal<FeatureKey | null>(null);
const drawerOpenSignal = signal<boolean>(false);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 60_000, refetchOnWindowFocus: false, retry: 1 }
  }
});

type MountState = {
  host: HTMLElement;
  shadowRoot: ShadowRoot;
  mountPoint: HTMLElement;
  reactRoot: Root;
  headObserver: MutationObserver | null;
};

let state: MountState | null = null;

let deprecatedStudentWarned = false;
/**
 * Some hosts still mount with the legacy `student` key instead of `user`.
 * Normalise it here so the rest of the widget can rely on `params.user`,
 * and warn once so integrators know they should migrate.
 */
function normalizeParams<T extends Partial<AppParams>>(input: T): T {
  if (!input || typeof input !== 'object') return input;
  if (!input.user && input.student) {
    if (!deprecatedStudentWarned) {
      console.warn(
        '[TutorWidget] `student` is deprecated, use `user` instead. ' +
          'Auto-mapping for now — update the host integration.'
      );
      deprecatedStudentWarned = true;
    }
    return { ...input, user: input.student };
  }
  return input;
}

function injectBaseStyles(shadow: ShadowRoot) {
  const sheet = new CSSStyleSheet();
  sheet.replaceSync(`${resetCss}\n${tokensCss}\n${globalsCss}`);
  shadow.adoptedStyleSheets = [...shadow.adoptedStyleSheets, sheet];
}

/**
 * Vite injects CSS-Module styles into document.head: directly in dev (HMR)
 * and via vite-plugin-css-injected-by-js in prod (lib mode emits CSS as a
 * separate file otherwise). Either way, they don't reach the shadow root,
 * so we mirror them here and keep observing for any added later.
 */
function mirrorHeadStylesToShadow(shadow: ShadowRoot): MutationObserver | null {
  const adopt = (node: Node) => {
    if (node instanceof HTMLStyleElement) {
      const clone = node.cloneNode(true) as HTMLStyleElement;
      clone.setAttribute('data-mirrored-from', 'head');
      shadow.appendChild(clone);
    } else if (node instanceof HTMLLinkElement && node.rel === 'stylesheet') {
      const clone = node.cloneNode(true) as HTMLLinkElement;
      shadow.appendChild(clone);
    }
  };

  document.head
    .querySelectorAll<HTMLStyleElement | HTMLLinkElement>(
      'style, link[rel="stylesheet"]'
    )
    .forEach(adopt);

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((m) => m.addedNodes.forEach(adopt));
  });
  observer.observe(document.head, { childList: true });
  return observer;
}

function applyTheme(mountPoint: HTMLElement, theme: AppParams['theme']) {
  if (!theme || theme === 'auto') {
    mountPoint.removeAttribute('data-theme');
  } else {
    mountPoint.setAttribute('data-theme', theme);
  }
}

function applyDrawerWidth(mountPoint: HTMLElement, width: AppParams['drawerWidth']) {
  if (width == null || width === '') {
    mountPoint.style.removeProperty('--phia-drawer-width');
    document.documentElement.style.removeProperty('--phia-drawer-width');
    return;
  }
  const value = typeof width === 'number' ? `${width}px` : width;
  // Set on both the shadow root (used by the drawer's own CSS) and on
  // <html> so the body's padding-right in push mode can read it too.
  mountPoint.style.setProperty('--phia-drawer-width', value);
  document.documentElement.style.setProperty('--phia-drawer-width', value);
}

function mountInternal(containerId: string, rawParams: AppParams) {
  const params = normalizeParams(rawParams);
  if (state) {
    paramsSignal.value = params;
    applyTheme(state.mountPoint, params.theme);
    applyDrawerWidth(state.mountPoint, params.drawerWidth);
    return { unmount: api.unmount };
  }
  // Embedded integrations (other platforms) load the script and call
  // mount() without adding the host div themselves — the legacy widget
  // used to create it on the fly, so we keep that behavior.
  let host = document.getElementById(containerId);
  if (!host) {
    host = document.createElement('div');
    host.id = containerId;
    document.body.appendChild(host);
  }

  const shadowRoot =
    host.shadowRoot ?? host.attachShadow({ mode: 'open' });
  injectBaseStyles(shadowRoot);
  const headObserver = mirrorHeadStylesToShadow(shadowRoot);

  const mountPoint = document.createElement('div');
  mountPoint.className = 'phia-root';
  applyTheme(mountPoint, params.theme);
  applyDrawerWidth(mountPoint, params.drawerWidth);
  shadowRoot.appendChild(mountPoint);

  configureApiClient(params);
  paramsSignal.value = params;

  const reactRoot = createRoot(mountPoint);
  reactRoot.render(
    <QueryClientProvider client={queryClient}>
      <WidgetShell
        paramsSignal={paramsSignal}
        openFeatureSignal={openFeatureSignal}
        drawerOpenSignal={drawerOpenSignal}
        featurePayloadSignal={featurePayloadSignal}
        shadowRoot={shadowRoot}
      />
    </QueryClientProvider>
  );

  state = { host, shadowRoot, mountPoint, reactRoot, headObserver };
  return { unmount: api.unmount };
}

/**
 * Course-context fields that the React tree caches heavily (activity queries,
 * thread lists, etc.). When any of these changes the cleanest behaviour is to
 * unmount + remount instead of letting stale data linger — mirrors the legacy
 * widget's hasSignificantParamsChanged (tutor-ai-widget/src/main.tsx:389).
 */
// HTM is @deprecated (no longer sent to /activity/*) but stays here so hosts
// that mutate it keep the same remount behaviour until it's fully removed.
const SIGNIFICANT_KEYS: (keyof AppParams)[] = ['AFO', 'MOD', 'UDI', 'HTM', 'route', 'count', 'language'];

function hasSignificantParamsChanged(prev: AppParams, next: AppParams): boolean {
  if (prev.user?.remote_id !== next.user?.remote_id) return true;
  return SIGNIFICANT_KEYS.some((key) => {
    const a = prev[key];
    const b = next[key];
    if (Array.isArray(a) || Array.isArray(b)) {
      return JSON.stringify(a ?? null) !== JSON.stringify(b ?? null);
    }
    return a !== b;
  });
}

const api: TutorWidgetAPI = {
  mount: mountInternal,
  updateParams(next) {
    const prev = paramsSignal.value;
    if (!prev) return;
    const normalizedNext = normalizeParams(next);
    const merged = { ...prev, ...normalizedNext };

    if (state && hasSignificantParamsChanged(prev, merged)) {
      const containerId = state.host.id;
      api.unmount();
      mountInternal(containerId, merged);
      return;
    }

    paramsSignal.value = merged;
    configureApiClient(merged);
    if (state) {
      applyTheme(state.mountPoint, merged.theme);
      applyDrawerWidth(state.mountPoint, merged.drawerWidth);
    }
  },
  unmount() {
    if (!state) return;
    state.headObserver?.disconnect();
    state.reactRoot.unmount();
    while (state.shadowRoot.firstChild) {
      state.shadowRoot.removeChild(state.shadowRoot.firstChild);
    }
    state = null;
    openFeatureSignal.value = null;
    drawerOpenSignal.value = false;
    featurePayloadSignal.value = null;
  },
  openFeature(feature) {
    openFeatureSignal.value = feature;
    drawerOpenSignal.value = true;
  },
  closeDrawer() {
    // Hide only — keep the active feature mounted so its state survives
    // until the next open. Use openFeature(null) or unmount() to reset.
    drawerOpenSignal.value = false;
  },
  getParams() {
    return paramsSignal.value ? { ...paramsSignal.value } : null;
  },
  getState() {
    return {
      mounted: state !== null,
      containerId: state?.host.id ?? null,
      params: paramsSignal.value ? { ...paramsSignal.value } : null,
      drawerOpen: drawerOpenSignal.value,
      openFeature: openFeatureSignal.value
    };
  }
};

window.TutorWidget = api;

if (import.meta.env.DEV) {
  console.log('[TutorWidget] script loaded — window.TutorWidget ready');
}
